/**
 * "View as user" — per-tab state.
 *
 * A Super Admin can open the app as another staff member to reproduce a fault
 * only that person sees. The impersonated session is a real Supabase session
 * minted by the `view-as` Edge Function, not a copy of the target's own, and it
 * lives in **sessionStorage** so that:
 *
 *   - it is scoped to the one tab, and cannot reach the admin's other tabs;
 *   - it cannot overwrite the admin's own session, which stays in localStorage
 *     under `moe-auth-token`;
 *   - closing the tab disposes of it, with the server-side ledger row swept
 *     afterwards by cron.
 *
 * Every accessor is wrapped, because sessionStorage throws in a private window
 * with site data blocked, and during thumbnail capture. A failure to read must
 * degrade to "not in view-as mode", never to a crash on boot.
 */

/** Where the view-as descriptor lives. Read synchronously at client creation. */
export const VIEW_AS_RECORD_KEY = "moe-viewas";

/**
 * Storage key for the impersonated Supabase session itself.
 *
 * Deliberately different from the admin's `moe-auth-token`. If these ever
 * collide, starting a view-as session signs the admin out of their own account
 * in every tab.
 */
export const VIEW_AS_AUTH_STORAGE_KEY = "moe-auth-viewas";

/** Fired when the read-only guard refuses a request. Detail: ViewAsBlockedDetail. */
export const VIEW_AS_BLOCKED_EVENT = "moe:view-as-blocked";

export interface ViewAsBlockedDetail {
  method: string;
  url: string;
  /** Short human label, e.g. "PATCH es_applications". */
  label: string;
}

export interface ViewAsRecord {
  impersonationId: string;
  targetUserId: string;
  targetName: string | null;
  targetEmail: string | null;
  targetRoleName: string | null;
  /** ISO timestamp. */
  startedAt: string;
  /** ISO timestamp. After this the session is swept server-side. */
  expiresAt: string;
  /** Whether the Super Admin has unblocked writes for this session. */
  actionsEnabled: boolean;
}

function isRecord(value: unknown): value is ViewAsRecord {
  if (!value || typeof value !== "object") return false;
  const r = value as Partial<ViewAsRecord>;
  return (
    typeof r.impersonationId === "string" &&
    r.impersonationId.length > 0 &&
    typeof r.targetUserId === "string" &&
    r.targetUserId.length > 0 &&
    typeof r.expiresAt === "string" &&
    !Number.isNaN(Date.parse(r.expiresAt))
  );
}

/**
 * The active view-as record for this tab, or null.
 *
 * An expired record is cleared rather than returned: the server has already
 * swept the session, so continuing to present the tab as the target would show
 * a banner over an app that is quietly failing every request. The tab boots as
 * the admin instead, which is recoverable and obvious.
 */
export function readViewAsRecord(): ViewAsRecord | null {
  if (typeof window === "undefined") return null;

  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(VIEW_AS_RECORD_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearViewAsRecord();
    return null;
  }

  if (!isRecord(parsed)) {
    clearViewAsRecord();
    return null;
  }

  if (Date.parse(parsed.expiresAt) <= Date.now()) {
    clearViewAsRecord();
    return null;
  }

  return parsed;
}

export function writeViewAsRecord(record: ViewAsRecord): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(VIEW_AS_RECORD_KEY, JSON.stringify(record));
  } catch (error) {
    // Without storage the whole feature cannot work, so say so rather than
    // proceeding into a half-state where the banner is missing but the session
    // is live.
    throw new Error(
      `Could not store the view-as session in this browser: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

/** Removes both the descriptor and the impersonated session it describes. */
export function clearViewAsRecord(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(VIEW_AS_RECORD_KEY);
  } catch {
    /* nothing useful to do */
  }
  try {
    window.sessionStorage.removeItem(VIEW_AS_AUTH_STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
}

export function isViewAsMode(): boolean {
  return readViewAsRecord() !== null;
}

/**
 * Raw presence of a view-as descriptor, expired or not.
 *
 * `readViewAsRecord` discards an expired record, which is right for deciding
 * which client to build but wrong for deciding what a sign-out *meant*: a
 * session that has just been swept still needs to be reported as "your view-as
 * session ended", not as an ordinary sign-out.
 */
export function hasViewAsRecord(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(VIEW_AS_RECORD_KEY) !== null;
  } catch {
    return false;
  }
}

/** Milliseconds until the session is swept, floored at zero. */
export function viewAsMillisRemaining(record: ViewAsRecord): number {
  return Math.max(0, Date.parse(record.expiresAt) - Date.now());
}
